type CashFlowDirection = "inflow" | "outflow";
type CashFlowSourceKind = "open_po" | "gipi" | "open_so" | "gosi" | "cbp_monthly_statement";

export interface CashFlowScheduledLineExportRow {
  direction: CashFlowDirection;
  source_kind: CashFlowSourceKind;
  document_ref: string;
  document_id: string | null;
  counterparty: string | null;
  amount: number;
  anchor_date: string | null;
  payment_terms_label: string | null;
  due_date: string | null;
  location_code: string | null;
  detail: string | null;
  link_path: string | null;
}

const CASH_FLOW_SOURCE_KIND_LABEL: Record<CashFlowSourceKind, string> = {
  open_po: "Open purchase order",
  gipi: "GIPI / TSH",
  cbp_monthly_statement: "CBP monthly statement",
  open_so: "Open sales order",
  gosi: "GOSI",
};

const CSV_HEADERS = [
  "Due date",
  "Direction",
  "Source",
  "Document",
  "Detail",
  "Location",
  "Counterparty",
  "Amount (USD)",
  "Payment terms",
  "Anchor date",
  "Document ID",
  "Hub link path",
] as const;

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const stringified = String(value);
  if (/[",\n\r]/.test(stringified)) {
    return `"${stringified.replace(/"/g, '""')}"`;
  }
  return stringified;
}

function formatDirection(direction: CashFlowDirection): string {
  return direction === "inflow" ? "Inflow" : "Outflow";
}

export function buildCashFlowScheduledLinesCsv(lines: CashFlowScheduledLineExportRow[]): string {
  const body = lines.map((line) =>
    [
      line.due_date,
      formatDirection(line.direction),
      CASH_FLOW_SOURCE_KIND_LABEL[line.source_kind],
      line.document_ref,
      line.detail,
      line.location_code,
      line.counterparty,
      line.amount,
      line.payment_terms_label,
      line.anchor_date,
      line.document_id,
      line.link_path,
    ]
      .map(escapeCsvCell)
      .join(","),
  );
  return [CSV_HEADERS.join(","), ...body].join("\r\n");
}

export function cashFlowScheduledLinesCsvFilename(from: string, to: string): string {
  return `cash-flow-scheduled-lines-${from}-to-${to}.csv`;
}

export function downloadCashFlowScheduledLinesCsv(
  lines: CashFlowScheduledLineExportRow[],
  from: string,
  to: string,
): void {
  const csv = buildCashFlowScheduledLinesCsv(lines);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = cashFlowScheduledLinesCsvFilename(from, to);
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
